import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { faSun } from '@fortawesome/free-solid-svg-icons';

const SecurityGovernance = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('policies');

  // Mock data for policies
  const policies = [
    {
      id: 'POL-001',
      name: 'Data Access Control',
      description: 'Controls who can access what data within the system',
      category: 'Access Control',
      lastReviewed: '2025-03-10',
      status: 'active',
      compliance: 'GDPR, HIPAA',
      owner: 'Security Team',
      riskLevel: 'High'
    },
    {
      id: 'POL-002',
      name: 'AI Model Usage',
      description: 'Guidelines for appropriate use of AI models',
      category: 'AI Ethics',
      lastReviewed: '2025-03-05',
      status: 'active',
      compliance: 'AI Ethics Framework',
      owner: 'AI Governance',
      riskLevel: 'Medium'
    },
    {
      id: 'POL-003',
      name: 'Data Retention',
      description: 'Rules for how long different types of data should be kept',
      category: 'Data Management',
      lastReviewed: '2025-02-28',
      status: 'active',
      compliance: 'GDPR, CCPA',
      owner: 'Data Team',
      riskLevel: 'High'
    },
    {
      id: 'POL-004',
      name: 'Agent Authorization',
      description: 'Defines what actions agents are allowed to perform',
      category: 'Agent Governance',
      lastReviewed: '2025-03-12',
      status: 'active',
      compliance: 'Internal',
      owner: 'AI Governance',
      riskLevel: 'High'
    },
    {
      id: 'POL-005',
      name: 'Audit Logging',
      description: 'Requirements for logging system and user actions',
      category: 'Compliance',
      lastReviewed: '2025-03-01',
      status: 'active',
      compliance: 'SOC2, HIPAA',
      owner: 'Security Team',
      riskLevel: 'Medium'
    },
  ];

  // Mock data for audit logs
  const auditLogs = [
    {
      id: 'LOG-001',
      timestamp: '2025-03-17 15:45:22',
      user: 'system',
      agent: 'Claims Processor',
      action: 'Processed claim #CL-78945',
      resource: 'Claims Database',
      status: 'success',
      details: 'Claim approved for $1,250.00'
    },
    {
      id: 'LOG-002',
      timestamp: '2025-03-17 15:30:18',
      user: 'jsmith',
      agent: 'N/A',
      action: 'Modified agent permissions',
      resource: 'Agent Studio',
      status: 'success',
      details: 'Updated permissions for Underwriting Agent'
    },
    {
      id: 'LOG-003',
      timestamp: '2025-03-17 15:15:05',
      user: 'system',
      agent: 'Document Processor',
      action: 'Accessed customer data',
      resource: 'Customer Database',
      status: 'success',
      details: 'Retrieved records for customer #12345'
    },
    {
      id: 'LOG-004',
      timestamp: '2025-03-17 14:55:30',
      user: 'system',
      agent: 'Fraud Detector',
      action: 'Flagged suspicious activity',
      resource: 'Claims Database',
      status: 'warning',
      details: 'Potential fraud detected in claim #CL-78930'
    },
    {
      id: 'LOG-005',
      timestamp: '2025-03-17 14:40:12',
      user: 'admin',
      agent: 'N/A',
      action: 'Modified system settings',
      resource: 'System Configuration',
      status: 'success',
      details: 'Updated data retention settings'
    },
  ];

  // Mock data for access controls
  const accessControls = [
    {
      id: 'ROLE-001',
      name: 'Claims Administrator',
      description: 'Full access to claims processing system',
      permissions: ['View Claims', 'Edit Claims', 'Approve Claims', 'Access Customer Data'],
      users: 12,
      lastModified: '2025-03-10',
      status: 'active'
    },
    {
      id: 'ROLE-002',
      name: 'Underwriter',
      description: 'Access to underwriting tools and customer data',
      permissions: ['View Applications', 'Edit Applications', 'Access Risk Models', 'Access Customer Data'],
      users: 18,
      lastModified: '2025-03-05',
      status: 'active'
    },
    {
      id: 'ROLE-003',
      name: 'AI Engineer',
      description: 'Ability to create and modify AI agents',
      permissions: ['Access Agent Studio', 'Edit Agents', 'View Performance Data', 'Access Model Hub'],
      users: 8,
      lastModified: '2025-03-12',
      status: 'active'
    },
    {
      id: 'ROLE-004',
      name: 'Compliance Officer',
      description: 'Access to audit logs and compliance tools',
      permissions: ['View Audit Logs', 'Run Compliance Reports', 'View Policies', 'Access Risk Assessment'],
      users: 5,
      lastModified: '2025-02-28',
      status: 'active'
    },
    {
      id: 'ROLE-005',
      name: 'Customer Service',
      description: 'Limited access to customer data and claims',
      permissions: ['View Customer Data', 'View Claims', 'View Policies'],
      users: 25,
      lastModified: '2025-03-01',
      status: 'active'
    },
  ];

  // Filter data based on search term
  const filteredPolicies = policies.filter(policy =>
    policy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    policy.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    policy.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAuditLogs = auditLogs.filter(log =>
    log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.agent.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.resource.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAccessControls = accessControls.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'pending': return 'warning';
      case 'inactive': return 'error';
      case 'success': return 'active';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  // Get risk level color
  const getRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'active';
      default: return 'default';
    }
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faSun}
          title="Security & Governance"
          description="Monitor and manage security policies, access controls, audit logs, and risk assessments.
          Ensure compliance with regulations and maintain proper governance of AI systems."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            {activeTab === 'applications' && (
              <SearchInput
                type="text"
                placeholder="Search claims..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}

            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button>Export Report</Button>
              <Button primary>Create Policy</Button>
            </div>
          </div>
        </PageHeaderWithInfo>


        <TabContainer>
          <TabList>
            <Tab active={activeTab === 'policies'} onClick={() => setActiveTab('policies')}>Policies</Tab>
            <Tab active={activeTab === 'access'} onClick={() => setActiveTab('access')}>Access Control</Tab>
            <Tab active={activeTab === 'audit'} onClick={() => setActiveTab('audit')}>Audit Logs</Tab>
            <Tab active={activeTab === 'risk'} onClick={() => setActiveTab('risk')}>Risk Assessment</Tab>
          </TabList>

          <ActionBar>
            <SearchInput
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button>Filter</Button>
            <Button>Sort</Button>
          </ActionBar>

          <TabContent active={activeTab === 'policies'}>
            <Card>
              <CardTitle>Security & Governance Policies</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Last Reviewed</TableHeader>
                    <TableHeader>Compliance</TableHeader>
                    <TableHeader>Owner</TableHeader>
                    <TableHeader>Risk Level</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredPolicies.map(policy => (
                    <TableRow key={policy.id}>
                      <TableCell>{policy.id}</TableCell>
                      <TableCell>{policy.name}</TableCell>
                      <TableCell>{policy.description}</TableCell>
                      <TableCell>{policy.category}</TableCell>
                      <TableCell>{policy.lastReviewed}</TableCell>
                      <TableCell>{policy.compliance}</TableCell>
                      <TableCell>{policy.owner}</TableCell>
                      <TableCell>
                        <StatusBadge status={getRiskColor(policy.riskLevel)}>
                          {policy.riskLevel}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(policy.status)}>
                          {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Edit</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Review</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'access'}>
            <Card>
              <CardTitle>Access Control Roles</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Role Name</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>Permissions</TableHeader>
                    <TableHeader>Users</TableHeader>
                    <TableHeader>Last Modified</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccessControls.map(role => (
                    <TableRow key={role.id}>
                      <TableCell>{role.id}</TableCell>
                      <TableCell>{role.name}</TableCell>
                      <TableCell>{role.description}</TableCell>
                      <TableCell>
                        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {role.permissions.join(', ')}
                        </div>
                      </TableCell>
                      <TableCell>{role.users}</TableCell>
                      <TableCell>{role.lastModified}</TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(role.status)}>
                          {role.status.charAt(0).toUpperCase() + role.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Edit</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Users</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'audit'}>
            <Card>
              <CardTitle>Audit Logs</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Timestamp</TableHeader>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Agent</TableHeader>
                    <TableHeader>Action</TableHeader>
                    <TableHeader>Resource</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Details</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredAuditLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell>{log.id}</TableCell>
                      <TableCell>{log.timestamp}</TableCell>
                      <TableCell>{log.user}</TableCell>
                      <TableCell>{log.agent}</TableCell>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>{log.resource}</TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(log.status)}>
                          {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.details}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'risk'}>
            <ContentGrid>
              <Card>
                <CardTitle>Risk Assessment</CardTitle>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span>Data Privacy Risk</span>
                        <span>High</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>
                        <div
                          style={{
                            width: '85%',
                            height: '8px',
                            background: '#FF3B30',
                            borderRadius: '4px'
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span>AI Ethics Risk</span>
                        <span>Medium</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>
                        <div
                          style={{
                            width: '60%',
                            height: '8px',
                            background: '#FFCC00',
                            borderRadius: '4px'
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span>Operational Risk</span>
                        <span>Medium</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>
                        <div
                          style={{
                            width: '55%',
                            height: '8px',
                            background: '#FFCC00',
                            borderRadius: '4px'
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span>Compliance Risk</span>
                        <span>Medium</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>
                        <div
                          style={{
                            width: '65%',
                            height: '8px',
                            background: '#FFCC00',
                            borderRadius: '4px'
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span>Security Risk</span>
                        <span>Low</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>
                        <div
                          style={{
                            width: '30%',
                            height: '8px',
                            background: '#34C759',
                            borderRadius: '4px'
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardTitle>Compliance Status</CardTitle>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(52, 199, 89, 0.1)', borderRadius: '8px' }}>
                      <span>GDPR</span>
                      <StatusBadge status="active">Compliant</StatusBadge>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(52, 199, 89, 0.1)', borderRadius: '8px' }}>
                      <span>HIPAA</span>
                      <StatusBadge status="active">Compliant</StatusBadge>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255, 204, 0, 0.1)', borderRadius: '8px' }}>
                      <span>CCPA</span>
                      <StatusBadge status="warning">Partial</StatusBadge>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(52, 199, 89, 0.1)', borderRadius: '8px' }}>
                      <span>SOC2</span>
                      <StatusBadge status="active">Compliant</StatusBadge>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255, 204, 0, 0.1)', borderRadius: '8px' }}>
                      <span>AI Ethics Framework</span>
                      <StatusBadge status="warning">In Progress</StatusBadge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </ContentGrid>

            <Card style={{ marginTop: '1.5rem' }}>
              <CardTitle>Risk Mitigation Actions</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Risk Area</TableHeader>
                    <TableHeader>Issue</TableHeader>
                    <TableHeader>Mitigation Plan</TableHeader>
                    <TableHeader>Owner</TableHeader>
                    <TableHeader>Due Date</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  <TableRow>
                    <TableCell>Data Privacy</TableCell>
                    <TableCell>Incomplete data anonymization</TableCell>
                    <TableCell>Implement enhanced anonymization protocols</TableCell>
                    <TableCell>Security Team</TableCell>
                    <TableCell>2025-04-15</TableCell>
                    <TableCell><StatusBadge status="warning">In Progress</StatusBadge></TableCell>
                    <TableCell><Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Update</Button></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>AI Ethics</TableCell>
                    <TableCell>Potential bias in underwriting model</TableCell>
                    <TableCell>Conduct bias audit and implement fairness metrics</TableCell>
                    <TableCell>AI Governance</TableCell>
                    <TableCell>2025-03-30</TableCell>
                    <TableCell><StatusBadge status="warning">In Progress</StatusBadge></TableCell>
                    <TableCell><Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Update</Button></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Compliance</TableCell>
                    <TableCell>CCPA data subject request handling</TableCell>
                    <TableCell>Implement automated DSR workflow</TableCell>
                    <TableCell>Compliance Team</TableCell>
                    <TableCell>2025-04-10</TableCell>
                    <TableCell><StatusBadge status="warning">In Progress</StatusBadge></TableCell>
                    <TableCell><Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Update</Button></TableCell>
                  </TableRow>
                </tbody>
              </Table>
            </Card>
          </TabContent>
        </TabContainer>

        <Card style={{ marginTop: '2rem' }}>
          <CardTitle>Governance Framework</CardTitle>
          <CardContent>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
              flexWrap: 'wrap',
              gap: '2rem'
            }}>
              <div style={{
                flex: '1 1 30%',
                minWidth: '250px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '10px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⍟</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Policies</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '250px' }}>
                  Establish rules and guidelines for system usage, data handling, and AI operations
                </div>
              </div>

              <div style={{
                flex: '1 1 30%',
                minWidth: '250px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '10px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⍟</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Controls</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '250px' }}>
                  Implement technical and procedural safeguards to enforce policies and mitigate risks
                </div>
              </div>

              <div style={{
                flex: '1 1 30%',
                minWidth: '250px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '10px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⍟</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Monitoring</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '250px' }}>
                  Continuously observe system behavior, user actions, and compliance status
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </PageContainer>
  );
};

export default SecurityGovernance;
